import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import './AnalyticsChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const AnalyticsChart = ({ data, title, dataKey, color = '#3498db' }) => {
  if (!data || !data.payload || data.payload.length === 0) {
    return (
      <div className="chart-container">
        <h3>{title}</h3>
        <div className="no-data">No data available for the selected period</div>
      </div>
    );
  }

  // Sort data by date
  const sortedData = [...data.payload].sort((a, b) => 
    new Date(a.day) - new Date(b.day)
  );

  // Handle different data key mappings for total_views
  const getDataValue = (item) => {
    if (dataKey === 'views') {
      return item.total_views || 0;
    }
    return item[dataKey] || 0;
  };

  const chartData = {
    labels: sortedData.map(item => {
      const date = new Date(item.day);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    }),
    datasets: [
      {
        label: title,
        data: sortedData.map(item => getDataValue(item)),
        borderColor: color,
        backgroundColor: color + '20',
        borderWidth: 2,
        pointBackgroundColor: color,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0.1,
        spanGaps: true, // Connect points across gaps
        fill: true // Add subtle fill under the line
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 12,
        cornerRadius: 8,
        displayColors: false,
        xAlign: 'left',
        yAlign: 'top',
        // xAlign: 'center',
        // yAlign : 'left',
        // external: function(context) {
        //   // Tooltip Element
        //   let tooltipEl = document.getElementById('chartjs-tooltip');

        //   if (!tooltipEl) {
        //     tooltipEl = document.createElement('div');
        //     tooltipEl.id = 'chartjs-tooltip';
        //     document.body.appendChild(tooltipEl);
        //   }

        //   // Hide if no tooltip
        //   const tooltipModel = context.tooltip;
        //   if (tooltipModel.opacity === 0) {
        //     tooltipEl.style.opacity = 0;
        //     return;
        //   }

        //   // Set Text
        //   if (tooltipModel.body) {
        //     const titleLines = tooltipModel.title || [];
        //     const bodyLines = tooltipModel.body.map(b => b.lines);

        //     let innerHtml = '';

        //     titleLines.forEach(function(title) {
        //       innerHtml += '<div style="font-weight: 600; margin-bottom: 4px;">' + title + '</div>';
        //     });

        //     bodyLines.forEach(function(body) {
        //       innerHtml += '<div>' + body + '</div>';
        //     });

        //     tooltipEl.innerHTML = innerHtml;
        //   }

        //   // Position tooltip in top-right corner of chart
        //   const chartCanvas = context.chart.canvas;
        //   const chartContainer = chartCanvas.closest('.chart-container-chartjs');
        //   const containerRect = chartContainer ? chartContainer.getBoundingClientRect() : chartCanvas.getBoundingClientRect();
          
        //   tooltipEl.style.opacity = 1;
        //   tooltipEl.style.position = 'fixed';
        //   tooltipEl.style.left = (containerRect.right - 220) + 'px';
        //   tooltipEl.style.top = containerRect.top + 10 + 'px';
        //   tooltipEl.style.pointerEvents = 'none';
        //   tooltipEl.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
        //   tooltipEl.style.color = '#fff';
        //   tooltipEl.style.padding = '12px';
        //   tooltipEl.style.borderRadius = '8px';
        //   tooltipEl.style.fontFamily = 'sans-serif';
        //   tooltipEl.style.fontSize = '12px';
        //   tooltipEl.style.zIndex = '1000';
        // },
        callbacks: {
          title: function(context) {
            return context[0]?.label || '';
          },
          label: function(context) {
            const dataIndex = context.dataIndex;
            const originalData = sortedData[dataIndex];
            return `Date: ${originalData.day} | ${title}: ${context.formattedValue}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: true,
          color: '#e1e8ed',
          drawBorder: false
        },
        ticks: {
          color: '#6c757d',
          font: {
            size: 11
          },
          maxRotation: 45,
          minRotation: 45
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          color: '#e1e8ed',
          drawBorder: false
        },
        ticks: {
          color: '#6c757d',
          font: {
            size: 11
          },
          callback: function(value) {
            if (value >= 1000) {
              return (value / 1000).toFixed(1) + 'k';
            }
            return value;
          }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    },
    elements: {
      point: {
        hoverRadius: 8
      }
    }
  };

  return (
    <div className="chart-container">
      <h3>{title}</h3>
      <div className="chart-wrapper">
        <div className="chart-container-chartjs">
          <Line data={chartData} options={options} height={300} />
        </div>
      </div>
      <div className="chart-summary">
        <span className="total-label">
          Total: {data.total_views_received || data.total_like_received || data.total_coins || data.total_comments || data.total_views || data.total_time_spended || 0}
        </span>
      </div>
    </div>
  );
};

export default AnalyticsChart;
